import { IconSet } from '@postscript/components';
import {
  FB_BASIC_CAMPAIGNS,
  NAV_UPDATES_SHOPPER,
} from 'components/admin/utils/feature-flags';
import { useInfinityTestingBillingStatus } from 'components/billing/modules/plans/hooks/useInfinityTestingBillingStatus';
import CtaBox from 'components/flowBuilder/components/CtaBox';
import { FlowWizardOptions } from 'components/flowBuilder/constants/trackingEvents';
import {
  FlowType,
  FlowWizardOptionsType,
  SubmitCreateFlowArgs,
} from 'components/flowBuilder/types';
import { usePSLabs } from 'controllers/contexts/labsFeatures';
import { useEntitlements } from 'providers/entitlements/EntitlementsProvider';
import { EntitlementKeys } from 'providers/entitlements/types';
import { useCallback, useMemo } from 'react';
import styled from 'styled-components';
import {
  COMMON_EVENT_NAMES,
  logEvent,
  MODAL_TYPES,
  PRODUCT_AREAS,
} from 'utils/events';
import { useITCUpsellExperiment } from '../hooks/useITCUpsellExperiment';
import { parseFlowType } from '../utils/flows';
import CreateFlowGridExperiment from './CreateFlowGridExperiment';

const StyledGridWrapper = styled.div<{
  itemCount?: number;
  variant?: 'modal' | 'page';
}>`
  display: grid;
  justify-content: ${({ variant }) =>
    variant === 'modal' ? 'center' : 'start'};
  gap: var(--spacing-4);
  grid-template-columns: ${({ itemCount, variant }) =>
    `repeat(${variant === 'modal' ? itemCount : 'auto-fit'}, 288px)`};
`;

const StyledCtaBoxWrapper = styled.div<{ order?: number }>`
  ${({ order }) => order !== undefined && `order: ${order};`}
  div:first-child {
    background-color: var(--surface-bkg-color);
  }
`;

interface CreateFlowGridProps {
  variant?: 'modal' | 'page';
  flowType: FlowType;
  handleSubmit: (args?: SubmitCreateFlowArgs) => void;
  onChooseTemplate: () => void;
}

const CAMPAIGN_ORDER_CONFIG = {
  [FlowWizardOptions.BASIC_CAMPAIGN]: 1,
  [FlowWizardOptions.CAMPAIGN_FLOW]: 2,
  [FlowWizardOptions.INFINITY_TEST_CAMPAIGN]: 4,
  [FlowWizardOptions.INFINITY_TEST_CAMPAIGN_CONTACT_SALES]: 4,
  [FlowWizardOptions.INFINITY_TEST_CAMPAIGN_LEARN_MORE]: 4,
  [FlowWizardOptions.CAMPAIGN_TEMPLATE]: 5,
  [FlowWizardOptions.LEGACY_CAMPAIGN]: 3,
} as const;

const AUTOMATION_ORDER_CONFIG = {
  [FlowWizardOptions.AUTOMATION]: 1,
  [FlowWizardOptions.AUTOMATION_TEMPLATE]: 2,
} as const;

const infinityBoxContent = {
  [FlowWizardOptions.INFINITY_TEST_CAMPAIGN]: {
    from: FlowWizardOptions.INFINITY_TEST_CAMPAIGN,
    label: 'Create an Infinity Test Campaign',
    tag: undefined,
  },
  [FlowWizardOptions.INFINITY_TEST_CAMPAIGN_CONTACT_SALES]: {
    from: FlowWizardOptions.INFINITY_TEST_CAMPAIGN_CONTACT_SALES,
    label: 'Contact Sales',
    tag: undefined,
  },
  [FlowWizardOptions.INFINITY_TEST_CAMPAIGN_LEARN_MORE]: {
    from: FlowWizardOptions.INFINITY_TEST_CAMPAIGN_LEARN_MORE,
    label: 'Learn More',
    tag: undefined,
  },
} as const;

export default function CreateFlowGrid({
  flowType,
  handleSubmit,
  onChooseTemplate,
  variant = 'page',
}: CreateFlowGridProps) {
  const { isAutomation, isCampaign } = parseFlowType(flowType);
  const { hasLabsFlag } = usePSLabs();
  const hasNavUpdatesShopper = hasLabsFlag(NAV_UPDATES_SHOPPER);
  const hasBasicCampaignsFlag = hasLabsFlag(FB_BASIC_CAMPAIGNS);
  const {
    variant: itcUpsellVariant,
    eligible: itcUpsellEligible,
    infinityBillingState,
  } = useITCUpsellExperiment();

  const modalType = isCampaign
    ? MODAL_TYPES.CREATE_CAMPAIGN
    : MODAL_TYPES.CREATE_AUTOMATION;

  // Campaign-specific infinity test logic — gate by billing status
  const { hasProduct, trialOngoing, trialAvailable, isEligible } =
    useInfinityTestingBillingStatus();

  const infinityTestOption = (() => {
    if (hasProduct || trialOngoing) {
      return FlowWizardOptions.INFINITY_TEST_CAMPAIGN;
    }
    if (isEligible && trialAvailable) {
      return FlowWizardOptions.INFINITY_TEST_CAMPAIGN_LEARN_MORE;
    }
    if (isEligible) {
      return FlowWizardOptions.INFINITY_TEST_CAMPAIGN_CONTACT_SALES;
    }
    return undefined;
  })();

  const { isEntitledTo } = useEntitlements();

  // Validate infinityTestOption is a valid key before using it
  const validInfinityTestOption =
    infinityTestOption != null && infinityTestOption in infinityBoxContent
      ? infinityTestOption
      : undefined;

  const hasStandardCampaignsEntitlement = isEntitledTo(
    EntitlementKeys.FB_CAMPAIGNS_STANDARD_USE,
  );

  // True for basic campaign feature flag shops, OR shops who can't see legacy campaigns
  const showBasicCampaignOption =
    hasBasicCampaignsFlag || !hasStandardCampaignsEntitlement;
  // False for custom cart users who can't see legacy campaigns. Also hidden
  // entirely when the new nav is on: legacy campaign creation lives only on
  // the /legacy page.
  const showLegacyCampaignOption =
    !hasBasicCampaignsFlag &&
    hasStandardCampaignsEntitlement &&
    !hasNavUpdatesShopper;
  const showInfinityTestOption = validInfinityTestOption != null;
  const showCampaignTemplateOption = isEntitledTo(
    EntitlementKeys.FB_TEMPLATES_CAMPAIGN_EXTENDED,
  );

  // Effective variant = what the user actually rendered. The hook reports
  // shop-level eligibility, but treatment only renders on the modal surface
  // (see modal-only gate below). Page renders always count as control.
  const effectiveExperimentVariant =
    itcUpsellVariant === 'treatment' && variant === 'modal'
      ? 'treatment'
      : 'control';

  const handleButtonClick = useCallback(
    (type: FlowWizardOptionsType) => {
      logEvent(COMMON_EVENT_NAMES.MODAL_CTA_CLICKED, {
        flow_type: flowType,
        modal_type: modalType,
        modal_selection_value: type,
        product_area: PRODUCT_AREAS.FLOW_BUILDER,
        experiment_variant: effectiveExperimentVariant,
        experiment_eligible: itcUpsellEligible,
      });

      if (
        type === FlowWizardOptions.CAMPAIGN_TEMPLATE ||
        type === FlowWizardOptions.AUTOMATION_TEMPLATE
      ) {
        onChooseTemplate();
        return;
      }

      handleSubmit({ from: type });
    },
    [
      flowType,
      modalType,
      onChooseTemplate,
      handleSubmit,
      effectiveExperimentVariant,
      itcUpsellEligible,
    ],
  );

  const itemCount = useMemo(() => {
    if (isAutomation) return 2;
    if (isCampaign && !showInfinityTestOption && !showCampaignTemplateOption)
      return 2;
    if (variant === 'modal' && showInfinityTestOption) return 2;
    return 3;
  }, [
    isAutomation,
    isCampaign,
    variant,
    showCampaignTemplateOption,
    showInfinityTestOption,
  ]);

  // Campaign-specific options
  if (isCampaign) {
    // Experiment is scoped to the modal surface only — the empty-state page
    // always renders the control layout so the campaigns-table empty-state
    // UX is not tangled into this measurement.
    if (effectiveExperimentVariant === 'treatment') {
      return (
        <CreateFlowGridExperiment
          flowType={flowType}
          handleSubmit={handleSubmit}
          onChooseTemplate={onChooseTemplate}
          showBasicCampaignOption={showBasicCampaignOption}
          showCampaignTemplateOption={showCampaignTemplateOption}
          showLegacyCampaignOption={showLegacyCampaignOption}
          experimentEligible={itcUpsellEligible}
          infinityBillingState={infinityBillingState}
        />
      );
    }

    return (
      <StyledGridWrapper variant={variant} itemCount={itemCount}>
        {showBasicCampaignOption && (
          <StyledCtaBoxWrapper
            order={CAMPAIGN_ORDER_CONFIG[FlowWizardOptions.BASIC_CAMPAIGN]}
          >
            <CtaBox
              cta={{
                dataCy: 'create-basic-campaign-button',
                label: 'Create a Basic Campaign',
                handleClick: () =>
                  handleButtonClick(FlowWizardOptions.BASIC_CAMPAIGN),
              }}
              description="Send a single-message campaign to a segment."
              icon={IconSet.Announcement}
              title="Basic Campaign"
            />
          </StyledCtaBoxWrapper>
        )}
        {showInfinityTestOption && validInfinityTestOption && (
          <StyledCtaBoxWrapper
            order={CAMPAIGN_ORDER_CONFIG[validInfinityTestOption]}
          >
            <CtaBox
              cta={{
                dataCy: 'create-infinity-test-campaign-button',
                label: infinityBoxContent[validInfinityTestOption].label,
                handleClick: () => {
                  if (
                    validInfinityTestOption ===
                    FlowWizardOptions.INFINITY_TEST_CAMPAIGN_LEARN_MORE
                  ) {
                    window.open(
                      'https://postscript.io/infinity-testing',
                      '_blank',
                      'noopener,noreferrer',
                    );
                    return;
                  }
                  handleButtonClick(
                    infinityBoxContent[validInfinityTestOption].from,
                  );
                },
              }}
              description="Continuously test AI message variants to maximize EPM or UCTR."
              icon={IconSet.Sparkles}
              tag={infinityBoxContent[validInfinityTestOption].tag}
              title="Infinity Testing Campaign"
            />
          </StyledCtaBoxWrapper>
        )}
        <StyledCtaBoxWrapper
          order={CAMPAIGN_ORDER_CONFIG[FlowWizardOptions.CAMPAIGN_FLOW]}
        >
          <CtaBox
            cta={{
              dataCy: 'create-flow-campaign-button',
              label: 'Create a Campaign Flow',
              handleClick: () =>
                handleButtonClick(FlowWizardOptions.CAMPAIGN_FLOW),
            }}
            description="Build a campaign flow from a blank canvas."
            icon={IconSet.Split}
            title="Create a Campaign Flow"
          />
        </StyledCtaBoxWrapper>
        {showCampaignTemplateOption && (
          <StyledCtaBoxWrapper
            order={CAMPAIGN_ORDER_CONFIG[FlowWizardOptions.CAMPAIGN_TEMPLATE]}
          >
            <CtaBox
              cta={{
                dataCy: 'choose-template-button',
                label: 'Choose a Template',
                handleClick: () =>
                  handleButtonClick(FlowWizardOptions.CAMPAIGN_TEMPLATE),
              }}
              description="Need some inspiration? Start with a prebuilt campaign."
              icon={IconSet.GridMasonry}
              title="Campaign Templates"
            />
          </StyledCtaBoxWrapper>
        )}
        {showLegacyCampaignOption && (
          <StyledCtaBoxWrapper
            order={CAMPAIGN_ORDER_CONFIG[FlowWizardOptions.LEGACY_CAMPAIGN]}
          >
            <CtaBox
              cta={{
                dataCy: 'create-legacy-flow-campaign-button',
                label: 'Create a Standard Campaign',
                handleClick: () =>
                  handleButtonClick(FlowWizardOptions.LEGACY_CAMPAIGN),
              }}
              description="Send a single-message campaign to a segment."
              icon={IconSet.Announcement}
              title="Standard Campaign"
            />
          </StyledCtaBoxWrapper>
        )}
      </StyledGridWrapper>
    );
  }

  // Automation-specific options
  return (
    <div>
      <StyledGridWrapper variant={variant} itemCount={itemCount}>
        <StyledCtaBoxWrapper
          order={AUTOMATION_ORDER_CONFIG[FlowWizardOptions.AUTOMATION]}
        >
          <CtaBox
            cta={{
              dataCy: 'create-flow-automation-button',
              label: 'Create an Automation Flow',
              handleClick: () =>
                handleButtonClick(FlowWizardOptions.AUTOMATION),
              variation: 'primary',
            }}
            description="Build an automation flow from a blank canvas."
            icon={IconSet.Split}
            title="Automation Flow"
          />
        </StyledCtaBoxWrapper>
        <StyledCtaBoxWrapper
          order={AUTOMATION_ORDER_CONFIG[FlowWizardOptions.AUTOMATION_TEMPLATE]}
        >
          <CtaBox
            cta={{
              dataCy: 'choose-automation-template-button',
              label: 'Choose a Template',
              handleClick: () =>
                handleButtonClick(FlowWizardOptions.AUTOMATION_TEMPLATE),
            }}
            description="Need some inspiration? Start with a prebuilt automation."
            icon={IconSet.GridMasonry}
            title="Automation Templates"
          />
        </StyledCtaBoxWrapper>
      </StyledGridWrapper>
    </div>
  );
}
